import React from "react";
import { Popover, Box, Typography, Button } from "@mui/material";
import { SvgIconComponent } from "@mui/icons-material";
import {
  Church as ChurchIcon,
  Home as HomeIcon,
  Place as PlaceIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";

export interface LocationOption {
  id: string;
  name: string;
  description: string;
  icon: SvgIconComponent;
  iconBg: string;
  iconColor: string;
}

interface LocationPickerProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  value: string;
  onChange: (location: string) => void;
  locations?: LocationOption[];
  title?: string;
}

const DEFAULT_LOCATIONS: LocationOption[] = [
  {
    id: "church",
    name: "Church",
    description: "Traditional church service",
    icon: ChurchIcon,
    iconBg: "#DBEAFE",
    iconColor: "#2563EB",
  },
  {
    id: "home",
    name: "Home",
    description: "Private residence service",
    icon: HomeIcon,
    iconBg: "#DCFCE7",
    iconColor: "#16A34A",
  },
  {
    id: "burial-ground",
    name: "Burial Ground",
    description: "Cemetery or memorial park",
    icon: PlaceIcon,
    iconBg: "#FFE4E6",
    iconColor: "#E11D48",
  },
  {
    id: "other",
    name: "Other",
    description: "Custom location",
    icon: BusinessIcon,
    iconBg: "#FEF3C7",
    iconColor: "#D97706",
  },
];

const LocationPicker: React.FC<LocationPickerProps> = ({
  open,
  anchorEl,
  onClose,
  value,
  onChange,
  locations = DEFAULT_LOCATIONS,
  title = "Suggested locations",
}) => {
  const handleLocationClick = (locationName: string) => {
    onChange(locationName);
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      PaperProps={{
        sx: {
          borderRadius: { xs: 3, md: 4 },
          p: { xs: 2, md: 2.5 },
          width: { xs: 320, md: 380 },
          maxHeight: { xs: "100%", lg: 300 },
          mt: 1,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontSize: { xs: "0.938rem", md: "1rem" },
          fontWeight: 600,
          mb: 1.5,
        }}
      >
        {title}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {locations.map((location) => {
          const Icon = location.icon;
          const isSelected = value === location.name;

          return (
            <Button
              key={location.id}
              onClick={() => handleLocationClick(location.name)}
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                borderRadius: 2,
                textTransform: "none",
                justifyContent: "flex-start",
                bgcolor: isSelected ? "grey.100" : "transparent",
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor: "grey.50",
                },
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  bgcolor: location.iconBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon sx={{ fontSize: 24, color: location.iconColor }} />
              </Box>
              <Box sx={{ flex: 1, textAlign: "left" }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    color: "text.primary",
                  }}
                >
                  {location.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                  }}
                >
                  {location.description}
                </Typography>
              </Box>
            </Button>
          );
        })}
      </Box>
    </Popover>
  );
};

export default LocationPicker;
